/**
 * Relay-only ICE probe at login: a throwaway RTCPeerConnection with `iceTransportPolicy:
 * 'relay'` and the session's TURN credential must produce at least one relay candidate within
 * the timeout. Failure is the amber self-check item; the phone retries every 60 s.
 */
import type { TurnCredentials } from '@ccphone/contracts';

export interface ProbePc {
  createDataChannel(label: string): unknown;
  createOffer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(d: RTCSessionDescriptionInit): Promise<void>;
  close(): void;
  onicecandidate: ((ev: { candidate: { candidate: string; type?: string } | null }) => void) | null;
}

export type TurnProbeResult = 'ok' | 'unreachable' | 'unsupported';

export async function probeTurn(
  turn: Pick<TurnCredentials, 'urls' | 'username' | 'credential'>,
  timeoutMs = 5000,
  pcFactory: (cfg: RTCConfiguration) => ProbePc = (cfg) => new RTCPeerConnection(cfg) as unknown as ProbePc,
): Promise<TurnProbeResult> {
  let pc: ProbePc;
  try {
    pc = pcFactory({ iceServers: [{ urls: turn.urls, username: turn.username, credential: turn.credential }], iceTransportPolicy: 'relay' });
  } catch {
    return 'unsupported';
  }
  return new Promise<TurnProbeResult>((resolve) => {
    let done = false;
    const finish = (r: TurnProbeResult): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        pc.close();
      } catch {
        /* ignore */
      }
      resolve(r);
    };
    const timer = setTimeout(() => finish('unreachable'), timeoutMs);
    pc.onicecandidate = (ev) => {
      const c = ev.candidate;
      if (!c) return;
      if (c.type === 'relay' || /\btyp relay\b/.test(c.candidate)) finish('ok');
    };
    try {
      pc.createDataChannel('probe');
      pc.createOffer()
        .then((o) => pc.setLocalDescription(o))
        .catch(() => finish('unsupported'));
    } catch {
      finish('unsupported');
    }
  });
}
